/**
 * Case Screenshots API — per-case AtWork screenshot tracking.
 *
 * Files that have a non-null `case_count` are required to have one
 * AtWork screenshot per case before they can transition from
 * "Signed & Sent to NB" to "Completed". Each screenshot is recorded
 * here and stored in the `screenshots` Supabase Storage bucket under
 * the deterministic prefix `case-screenshots/{file_id}/{case_index}.{ext}`.
 *
 * The completion gate is enforced server-side by a database trigger
 * (see supabase/migrations/07_multi_case_screenshots.sql). The helpers
 * below are for the UX layer — listing, uploading, replacing and
 * orphan-cleaning rows when an adviser reduces the case count.
 */

import supabase from './supabaseClient';

const BUCKET = 'screenshots';
const PATH_PREFIX = 'case-screenshots';

function mapRow(row) {
  return {
    id: row.id,
    fileId: row.file_id,
    caseIndex: row.case_index,
    screenshotUrl: row.screenshot_url,
    uploadedAt: row.uploaded_at,
    uploadedBy: row.uploaded_by || '',
    verificationStatus: row.verification_status || 'pending',
    verificationDetails: row.verification_details || null,
    overrideBy: row.override_by || null,
    overrideAt: row.override_at || null,
    overrideReason: row.override_reason || null,
  };
}

function buildPath(fileRowId, caseIndex, ext) {
  const cleanExt = (ext || 'png').replace(/[^a-z0-9]/gi, '').toLowerCase() || 'png';
  return `${PATH_PREFIX}/${fileRowId}/${caseIndex}.${cleanExt}`;
}

/**
 * Fetch every case screenshot row for a file.
 * Returns an array sorted by case_index ascending.
 */
export async function getCaseScreenshotsForFile(fileRowId) {
  const { data, error } = await supabase
    .from('case_screenshots')
    .select('*')
    .eq('file_id', fileRowId)
    .order('case_index', { ascending: true });

  if (error) throw new Error(`Failed to fetch case screenshots: ${error.message}`);
  return (data || []).map(mapRow);
}

/**
 * Custom error thrown when the AI verification step rejects an upload
 * (Layer 2 hard reject, HTTP 422 from /api/verify-screenshot). The UI
 * uses this to render a per-row error and, for advisors, expose the
 * "Override and upload anyway" affordance.
 */
export class CaseScreenshotVerificationError extends Error {
  constructor({ kind, message, details, expectedClientName }) {
    super(message);
    this.name = 'CaseScreenshotVerificationError';
    this.kind = kind; // 'l2_failed' | 'unavailable'
    this.details = details || null;
    this.expectedClientName = expectedClientName || null;
  }
}

/**
 * Call the verify-screenshot Pages Function. Returns the parsed
 * response on success (verification_status === 'passed' | 'flagged' |
 * 'l2_overridden'); throws CaseScreenshotVerificationError otherwise.
 */
async function callVerificationEndpoint({
  fileRowId,
  caseIndex,
  clientName,
  caseLabel,
  file,
  override,
  overrideReason,
}) {
  const session = (await supabase.auth.getSession()).data.session;
  const token = session && session.access_token;
  if (!token) {
    throw new CaseScreenshotVerificationError({
      kind: 'unavailable',
      message: 'You must be signed in to upload a screenshot.',
    });
  }

  const fd = new FormData();
  fd.append('file_id', fileRowId);
  fd.append('case_index', String(caseIndex));
  fd.append('client_name', clientName || '');
  fd.append('case_label', caseLabel || '');
  fd.append('image', file, file.name || 'screenshot.png');
  if (override) {
    fd.append('override', 'true');
    fd.append('override_reason', overrideReason || '');
  }

  const res = await fetch('/api/verify-screenshot', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });

  // 422 → Layer 2 hard reject
  if (res.status === 422) {
    const body = await res.json().catch(() => ({}));
    throw new CaseScreenshotVerificationError({
      kind: 'l2_failed',
      message: body.error || 'Verification failed.',
      details: body.details || null,
      expectedClientName: clientName,
    });
  }

  // 503 → verification system temporarily unavailable
  if (res.status === 503) {
    const body = await res.json().catch(() => ({}));
    throw new CaseScreenshotVerificationError({
      kind: 'unavailable',
      message: body.error || 'Verification system temporarily unavailable.',
    });
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new CaseScreenshotVerificationError({
      kind: 'unavailable',
      message: body.error || `Verification request failed (HTTP ${res.status}).`,
    });
  }

  return res.json();
}

/**
 * Upload (or replace) a screenshot for a specific case.
 *
 * Verification runs at upload time via /api/verify-screenshot. If
 * Layer 2 fails (and no override is supplied), CaseScreenshotVerificationError
 * is thrown with kind === 'l2_failed' and nothing is persisted.
 * Otherwise the storage object is uploaded and the case_screenshots
 * row is upserted with the verification status returned by the API.
 *
 * @param {object} params
 * @param {string} params.fileRowId        — files.id (UUID)
 * @param {number} params.caseCount        — files.case_count (validation)
 * @param {number} params.caseIndex        — 0-based case index
 * @param {string} params.clientName       — files.client_name (verification)
 * @param {string} params.caseLabel        — files.case_labels[caseIndex]
 * @param {File}   params.file             — the image file to upload
 * @param {string} params.uploadedBy       — uploader email (audit)
 * @param {boolean} [params.override]      — advisor override flag
 * @param {string}  [params.overrideReason]— required when override is true
 * @returns {Promise<object>} the stored screenshot row
 */
export async function uploadCaseScreenshot({
  fileRowId,
  caseCount,
  caseIndex,
  clientName,
  caseLabel,
  file,
  uploadedBy,
  override = false,
  overrideReason = null,
}) {
  if (!fileRowId) throw new Error('fileRowId is required.');
  if (!file) throw new Error('A screenshot file is required.');
  if (!uploadedBy) throw new Error('uploadedBy is required.');

  // Defence-in-depth: the DB has its own constraints, but bail early
  // if the caller is asking for a case index that doesn't exist.
  if (
    typeof caseCount !== 'number' ||
    caseCount < 1 ||
    caseIndex < 0 ||
    caseIndex >= caseCount
  ) {
    throw new Error(
      `Invalid case index ${caseIndex} for file with ${caseCount} case(s).`
    );
  }

  // 1. Run AI verification first. On Layer 2 failure (and no
  // override), this throws and we never touch storage or the DB.
  const verification = await callVerificationEndpoint({
    fileRowId,
    caseIndex,
    clientName,
    caseLabel,
    file,
    override,
    overrideReason,
  });

  // 2. Upload (or overwrite) the storage object now that verification
  // has accepted (or been overridden).
  const ext = (file.name.split('.').pop() || 'png').toLowerCase();
  const path = buildPath(fileRowId, caseIndex, ext);
  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, {
      contentType: file.type || 'image/png',
      upsert: true,
    });
  if (uploadError) {
    throw new Error(`Storage upload failed: ${uploadError.message}`);
  }

  const { data: urlData, error: urlError } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, 60 * 60 * 24 * 365);
  if (urlError) {
    throw new Error(`Failed to generate signed URL: ${urlError.message}`);
  }
  const screenshotUrl = urlData.signedUrl;

  // 3. Upsert the database row keyed on (file_id, case_index).
  // Replace flow: any prior override fields are cleared because
  // re-verification supersedes any previous override.
  const isOverride = verification.verification_status === 'l2_overridden';
  const row = {
    file_id: fileRowId,
    case_index: caseIndex,
    screenshot_url: screenshotUrl,
    uploaded_at: new Date().toISOString(),
    uploaded_by: uploadedBy,
    verification_status: verification.verification_status,
    verification_details: verification.details || null,
    override_by: isOverride ? (verification.override_by || uploadedBy) : null,
    override_at: isOverride ? new Date().toISOString() : null,
    override_reason: isOverride ? (verification.override_reason || overrideReason) : null,
  };

  const { data, error } = await supabase
    .from('case_screenshots')
    .upsert(row, { onConflict: 'file_id,case_index' })
    .select()
    .single();
  if (error) {
    throw new Error(`Failed to record case screenshot: ${error.message}`);
  }
  return mapRow(data);
}

/**
 * Clear a Layer 3 flag on an existing case screenshot. Advisor only —
 * the database trigger enforce_screenshot_override_advisor_only enforces
 * this at the DB layer using auth.jwt(), so a non-advisor request fails
 * even if the UI is bypassed. Verification details are preserved as the
 * audit trail.
 */
export async function overrideFlag({ fileRowId, caseIndex, reason, overrideBy }) {
  const trimmed = (reason || '').trim();
  if (!trimmed) throw new Error('A reason is required to clear a flag.');
  if (!overrideBy) throw new Error('overrideBy is required.');

  const { data, error } = await supabase
    .from('case_screenshots')
    .update({
      verification_status: 'flag_overridden',
      override_by: overrideBy,
      override_at: new Date().toISOString(),
      override_reason: trimmed,
    })
    .eq('file_id', fileRowId)
    .eq('case_index', caseIndex)
    .eq('verification_status', 'flagged')
    .select()
    .single();
  if (error) {
    throw new Error(`Failed to clear flag: ${error.message}`);
  }
  return mapRow(data);
}

/**
 * Count Layer 3 flagged screenshots per file. Used by both dashboards
 * to drive the small amber dot on kanban cards. Returns a map of
 * { [files.id]: count } for files in the `signed` stage with at least
 * one row whose verification_status === 'flagged'.
 */
export async function getFlaggedCountsByFile() {
  const { data, error } = await supabase
    .from('case_screenshots')
    .select('file_id')
    .eq('verification_status', 'flagged');
  if (error) {
    throw new Error(`Failed to fetch flag counts: ${error.message}`);
  }
  const counts = {};
  for (const row of data || []) {
    counts[row.file_id] = (counts[row.file_id] || 0) + 1;
  }
  return counts;
}

/**
 * Remove the screenshot for a single case (storage object + DB row).
 * Tolerates missing storage objects so it can be used to clean up
 * partial state from an interrupted upload.
 */
export async function deleteCaseScreenshot(fileRowId, caseIndex) {
  // Storage path uses a known prefix but unknown extension — list and
  // remove every object that matches.
  const folder = `${PATH_PREFIX}/${fileRowId}`;
  const { data: objects, error: listError } = await supabase.storage
    .from(BUCKET)
    .list(folder);
  if (!listError && objects) {
    const matching = objects
      .filter((o) => o.name.startsWith(`${caseIndex}.`))
      .map((o) => `${folder}/${o.name}`);
    if (matching.length > 0) {
      await supabase.storage.from(BUCKET).remove(matching);
    }
  }

  const { error } = await supabase
    .from('case_screenshots')
    .delete()
    .eq('file_id', fileRowId)
    .eq('case_index', caseIndex);
  if (error) {
    throw new Error(`Failed to delete case screenshot: ${error.message}`);
  }
}

/**
 * Apply a new case structure to a file.
 *
 * If the new count is smaller than the current count, every
 * case_screenshots row (and storage object) for the dropped indices
 * is deleted in the same logical operation, so the structure cannot
 * drift out of sync. The advisor confirms the destructive part of
 * this in the UI before calling.
 *
 * @param {object} params
 * @param {string}   params.fileRowId
 * @param {number}   params.newCount
 * @param {string[]} params.newLabels
 * @returns {Promise<{ deletedIndices: number[] }>}
 */
export async function updateCaseStructure({ fileRowId, newCount, newLabels }) {
  if (!fileRowId) throw new Error('fileRowId is required.');
  if (!Number.isInteger(newCount) || newCount < 1 || newCount > 10) {
    throw new Error('case_count must be an integer between 1 and 10.');
  }
  if (!Array.isArray(newLabels) || newLabels.length !== newCount) {
    throw new Error('case_labels must be an array of the same length as case_count.');
  }
  const trimmedLabels = newLabels.map((s) => (typeof s === 'string' ? s.trim() : ''));
  if (trimmedLabels.some((s) => !s)) {
    throw new Error('Every case label must be non-empty.');
  }

  // Identify orphan screenshots (case_index >= newCount) and clean them up
  // before tightening the column. Doing this first keeps the screenshots
  // table consistent with case_count if the file UPDATE later fails.
  const { data: existing, error: existingErr } = await supabase
    .from('case_screenshots')
    .select('case_index')
    .eq('file_id', fileRowId);
  if (existingErr) {
    throw new Error(`Failed to read existing screenshots: ${existingErr.message}`);
  }

  const orphanIndices = (existing || [])
    .map((r) => r.case_index)
    .filter((i) => i >= newCount);

  for (const idx of orphanIndices) {
    await deleteCaseScreenshot(fileRowId, idx);
  }

  const update = {
    case_count: newCount,
    case_labels: trimmedLabels,
  };

  const { error: updErr } = await supabase
    .from('files')
    .update(update)
    .eq('id', fileRowId);
  if (updErr) {
    throw new Error(`Failed to update case structure: ${updErr.message}`);
  }

  return { deletedIndices: orphanIndices };
}
